import { motion } from "framer-motion";
import { fadeUp, staggerChild, staggerContainer } from "../lib/motion";

const ARCH_IMG =
  "https://images.unsplash.com/photo-1526289034009-0240ddb68ce3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

const noLonger = [
  "Content alone to drive revenue",
  "Manual follow-up to convert demand",
  "Memory and improvisation to run operations",
  "One offer carrying the entire business",
  "You being involved in every conversion touchpoint",
];

const instead = [
  "The right offers exist",
  "The path to purchase is clear",
  "Monetization is intentional",
  "Growth has structure",
  "Demand can be absorbed without the business breaking",
];

export const Mechanism = () => {
  return (
    <section
      data-testid="mechanism-section"
      className="relative py-24 md:py-32 lg:py-44 bg-[#101116] border-y border-[#282930] overflow-hidden"
    >
      <div className="px-6 md:px-12 lg:px-20 max-w-[1500px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-7">
            <motion.div {...fadeUp} className="flex items-center gap-3 mb-6">
              <span className="eyebrow">04 — Mechanism</span>
              <span className="block w-8 h-px bg-[#282930]" />
            </motion.div>
            <motion.h2
              {...fadeUp}
              className="font-display text-3xl sm:text-4xl lg:text-[52px] leading-[1.05] tracking-tight"
            >
              Creator-led on the front end.
              <br />
              <span className="italic text-[#C9A26B]">
                System-led on the backend.
              </span>
            </motion.h2>
            <motion.p
              {...fadeUp}
              className="mt-8 text-[#A8A39D] text-base md:text-lg leading-relaxed max-w-[54ch]"
            >
              The model is simple. You stay visible. We build what supports the
              visibility.
            </motion.p>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 1.04 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative aspect-[4/5] overflow-hidden border border-[#282930]"
            >
              <img
                src={ARCH_IMG}
                alt="Architectural detail"
                className="w-full h-full object-cover grayscale contrast-[1.05] brightness-90"
              />
              <div
                className="absolute inset-0 mix-blend-overlay"
                style={{ background: "rgba(201,162,107,0.18)" }}
              />
              <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end text-[10px] tracking-[0.2em] uppercase text-[#F4EFE6]/80">
                <span>FIG. 01</span>
                <span>Structural Backend</span>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="mt-20 lg:mt-28 grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <p className="eyebrow text-[#A8A39D] mb-6">Your business no longer relies on</p>
            <motion.ul {...staggerContainer} className="space-y-3">
              {noLonger.map((n) => (
                <motion.li
                  {...staggerChild}
                  key={n}
                  className="py-3 border-b border-[#1D1E24] text-[#A8A39D] line-through decoration-[#282930]"
                >
                  {n}
                </motion.li>
              ))}
            </motion.ul>
          </div>
          <div>
            <p className="eyebrow text-[#C9A26B] mb-6">Instead, we build a backend where</p>
            <motion.ul {...staggerContainer} className="space-y-3">
              {instead.map((n) => (
                <motion.li
                  {...staggerChild}
                  key={n}
                  className="py-3 border-b border-[#1D1E24] text-[#F4EFE6]"
                >
                  {n}
                </motion.li>
              ))}
            </motion.ul>
          </div>
        </div>

        <motion.p
          {...fadeUp}
          className="mt-16 max-w-3xl font-display text-2xl md:text-3xl leading-snug text-[#F4EFE6]"
        >
          This is what turns creator traction into{" "}
          <span className="italic text-[#C9A26B]">business leverage.</span>
        </motion.p>
      </div>
    </section>
  );
};

export default Mechanism;
